import { Node, mergeAttributes } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import debugFactory from "debug";
import { TeamingTiptapExtensionType } from "@teaming/common-enums";
import TeamingTiptapCommitment from "./TeamingTiptapCommitment.vue";

const debug = debugFactory( "teaming:tiptap:commitment" );

import { canSplit } from "prosemirror-transform";
import { TextSelection, Selection } from "prosemirror-state";

function getSplittedAttributes( extensionAttributes, typeName, attributes ) {
	return Object.fromEntries( Object
		.entries( attributes )
		.filter( ( [ name, ] ) => {
			const extensionAttribute = extensionAttributes.find( item => {
				return item.type === typeName && item.name === name;
			} );
			if ( !extensionAttribute ) {
				return false;
			}
			return extensionAttribute.attribute.keepOnSplit;
		} ) );
}

function defaultBlockAt( match ) {
	for ( let i = 0; i < match.edgeCount; i += 1 ) {
		const { type, } = match.edge( i );

		if ( type.isTextblock && !type.hasRequiredAttrs() ) {
			return type;
		}
	}
	return null;
}

export default Node.create( {
	name: "teaming-commitment",
	content: "text*",
	defining: true,
	group: "block",
	marks: "",
	draggable: true,

	addNodeView() {
		debug( "Executing addNodeView" );
		// eslint-disable-next-line new-cap
		return VueNodeViewRenderer( TeamingTiptapCommitment );
	},

	addOptions() {
		return {
			onTeamingExtensionCallback: () => {},
		};
	},

	parseHTML() {
		return [
			{
				tag: "teaming-commitment",
			},
		];
	},

	renderHTML( { HTMLAttributes, } ) {
		return [ "teaming-commitment", mergeAttributes( HTMLAttributes ), 0, ];
	},

	addCommands() {
		debug( "Executing addCommands" );
		return {
			setCommitment: () => ( { commands, } ) => {
				return commands.setNode( this.name );
			},
			toggleCommitment: attributes => ( { commands, } ) => {
				return commands.toggleNode( this.name, "paragraph", attributes );
			},
			unsetCommitment: () => ( { commands, } ) => {
				return commands.lift( this.name );
			},
			// adapted from prosemirror-commands exitCode method
			exitCommitment: () => ( { state, dispatch, } ) => {
				const { $head, $anchor, } = state.selection;
				if ( !$head.sameParent( $anchor ) ) {
					return false;
				}
				const above = $head.node( -1 );
				const after = $head.indexAfter( -1 );
				const type = defaultBlockAt( above.contentMatchAt( after ) );
				if ( !above.canReplaceWith( after, after, type ) ) {
					return false;
				}
				if ( dispatch ) {
					const pos = $head.after();
					const tr = state.tr.replaceWith( pos, pos, type.createAndFill() );
					tr.setSelection( Selection.near( tr.doc.resolve( pos ), 1 ) );
					dispatch( tr.scrollIntoView() );
				}
				return true;
			},
			// adapted from tiptap-core splitBlock method
			splitCommitment: () => ( {
				tr,
				dispatch,
				editor,
			} ) => {
				const { selection, } = tr;
				const { $from, } = selection;
				const extensionAttributes = editor.extensionManager.attributes;
				const newAttributes = getSplittedAttributes(
					extensionAttributes,
					$from.node().type.name,
					$from.node().attrs
				);
				const types = [
					{
						type: $from.parent.type,
						attrs: newAttributes,
					},
				];

				if ( dispatch ) {
					if ( selection instanceof TextSelection ) {
						tr.deleteSelection();
					}

					const defaultBlock = defaultBlockAt( $from.node( -1 ).contentMatchAt( $from.indexAfter( -1 ) ) );

					const deflt = $from.depth === 0 ?
						undefined :
						defaultBlock;

					let can = canSplit( tr.doc, tr.mapping.map( $from.pos ), 1 );

					if (
						!can &&
						canSplit(
							tr.doc,
							tr.mapping.map( $from.pos ),
							1,
							deflt ?
								[ { type: deflt, }, ] :
								types
						)
					) {
						can = true;
					}

					if ( can ) {
						tr.split( tr.mapping.map( $from.pos ), 1, types );
					}

					tr.scrollIntoView();
				}

				return true;
			},
		};
	},

	addAttributes() {
		return {
			"due-date": {
				default: null,
				keepOnSplit: false,
			},
			"assigned-to": {
				default: null,
				keepOnSplit: false,
			},
			"assigned-by": {
				default: null,
				keepOnSplit: false,
			},
			"completed-date": {
				default: null,
				keepOnSplit: false,
			},
		};
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => {
				const { $anchor, } = this.editor.state.selection;
				if ( $anchor.parent.type.name !== this.name ) {
					return false;
				}
				const hasContent = !!$anchor.parent.content.size;
				return hasContent ?
					this.editor.commands.splitCommitment() :
					this.editor.commands.toggleCommitment();
			},
			ArrowDown: ( { editor, } ) => {
				const { state, } = editor;
				const { selection, doc, } = state;
				const { $from, empty, } = selection;

				if ( !empty || $from.parent.type !== this.type ) {
					return false;
				}

				const after = $from.after();

				if ( after === undefined ) {
					return false;
				}

				const nodeAfter = doc.nodeAt( after );

				if ( nodeAfter ) {
					editor.commands.focus( after );
					return false;
				}

				editor.commands.exitCommitment();

				return true;
			},
		};
	},

	onCreate() {
		const { $anchor, } = this.editor.state.selection;
		if ( $anchor.parent.type.name === this.name ) {
			this.options.onTeamingExtensionCallback( { nodeType: TeamingTiptapExtensionType.COMMITMENT, type: "create", } );
		}
	},

	onUpdate() {
		const { $anchor, } = this.editor.state.selection;
		if ( $anchor.parent.type.name === this.name ) {
			this.options.onTeamingExtensionCallback( { nodeType: TeamingTiptapExtensionType.COMMITMENT, type: "update", } );
		}
	},

} );
